import { formatCurrencyID, formatQuantity } from '../../utils/currency';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { CalendarIcon, Plus, Trash2 } from 'lucide-react';
import { Calendar } from '../ui/calendar';

export function POConfirmDialog({ alasanReject, alasanTidakSesuai, confirmType, formData, handleAddItem, handleConfirmPO, handleItemChange, handleRemoveItem, selectedPO, setAlasanReject, setAlasanTidakSesuai, setShowConfirm, setTanggalTerima, showConfirm, tanggalTerima }) {
  return (
    <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
      <DialogContent className={`${confirmType === 'diterima_adj' ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle>
            {confirmType === 'diterima' && 'Konfirmasi Penerimaan Barang'}
            {confirmType === 'diterima_adj' && 'Penerimaan Barang - Tidak Sesuai PO'}
            {confirmType === 'reject' && 'Reject Purchase Order'}
          </DialogTitle>
        </DialogHeader>

        {selectedPO && (
          <div className="space-y-4">
            {/* PO Info */}
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm"><strong>No. PO:</strong> {selectedPO.po_number}</p>
              <p className="text-sm"><strong>Supplier:</strong> {selectedPO.supplier_name}</p>
              <p className="text-sm"><strong>Total:</strong> Rp {selectedPO.total_amount.toLocaleString('id-ID')}</p>
            </div>

            {/* Reject Confirmation */}
            {confirmType === 'reject' && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-center">
                  <p className="text-red-700 font-semibold">Apakah Anda yakin ingin me-reject PO ini?</p>
                  <p className="text-sm text-red-600 mt-1">Tidak ada Tanda Terima Barang yang akan dibuat.</p>
                </div>

                <div className="space-y-2">
                  <Label>Alasan Reject *</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                    value={alasanReject}
                    onChange={(e) => setAlasanReject(e.target.value.toUpperCase())}
                    placeholder="MASUKKAN ALASAN REJECT..."
                    required
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => { setShowConfirm(false); setAlasanReject(''); }}>Batal</Button>
                  <Button variant="destructive" onClick={handleConfirmPO} data-testid="confirm-reject-btn" disabled={!alasanReject.trim()}>
                    Ya, Reject PO
                  </Button>
                </div>
              </div>
            )}

            {/* Sesuai PO Confirmation */}
            {confirmType === 'diterima' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                  <p className="text-green-700 font-semibold">Barang diterima sesuai dengan PO</p>
                </div>

                <div className="space-y-2">
                  <Label>Tanggal Terima Barang *</Label>
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {tanggalTerima ? format(tanggalTerima, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={tanggalTerima}
                        onSelect={setTanggalTerima}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowConfirm(false)}>Batal</Button>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={handleConfirmPO} data-testid="confirm-sesuai-btn">
                    Konfirmasi & Cetak TTB
                  </Button>
                </div>
              </div>
            )}

            {/* Tidak Sesuai PO Form */}
            {confirmType === 'diterima_adj' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-center">
                  <p className="text-yellow-700 font-semibold">Barang diterima TIDAK SESUAI dengan PO</p>
                  <p className="text-sm text-yellow-600 mt-1">Edit data item sesuai dengan barang yang diterima</p>
                </div>

                <div className="space-y-2">
                  <Label>Tanggal Terima Barang *</Label>
                  <Popover modal={false}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {tanggalTerima ? format(tanggalTerima, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[100]" align="start">
                      <Calendar
                        mode="single"
                        selected={tanggalTerima}
                        onSelect={setTanggalTerima}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Alasan Tidak Sesuai *</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                    value={alasanTidakSesuai}
                    onChange={(e) => setAlasanTidakSesuai(e.target.value.toUpperCase())}
                    placeholder="MASUKKAN ALASAN BARANG TIDAK SESUAI..."
                    required
                  />
                </div>

                {/* Editable Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Items (Edit sesuai barang diterima)</Label>
                    <Button type="button" size="sm" onClick={handleAddItem}>
                      <Plus className="h-4 w-4 mr-1" /> Tambah
                    </Button>
                  </div>
                  <div className="border rounded-lg p-3 space-y-3 max-h-[300px] overflow-y-auto">
                    {formData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end pb-2 border-b last:border-b-0">
                        <div className="col-span-5">
                          <Label className="text-xs">Nama Item</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                            placeholder="NAMA ITEM"
                            style={{ textTransform: 'uppercase' }}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Satuan</Label>
                          <Select 
                            value={item.satuan || 'pcs'} 
                            onValueChange={(value) => handleItemChange(index, 'satuan', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pcs">Pcs</SelectItem>
                              <SelectItem value="unit">Unit</SelectItem>
                              <SelectItem value="box">Box</SelectItem>
                              <SelectItem value="pack">Pack</SelectItem>
                              <SelectItem value="set">Set</SelectItem>
                              <SelectItem value="kg">Kg</SelectItem>
                              <SelectItem value="gram">Gram</SelectItem>
                              <SelectItem value="liter">Liter</SelectItem>
                              <SelectItem value="meter">Meter</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Qty</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={item.quantity_display ?? formatQuantity(item.quantity)}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Harga (Rp)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.unit_price_display ?? formatCurrencyID(item.unit_price)}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                          />
                        </div>
                        <div className="col-span-1">
                          {formData.items.length > 1 && (
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Updated Total */}
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
                  <div className="flex justify-between font-bold">
                    <span>Total Adjustment</span>
                    <span className="font-mono">
                      Rp {formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0).toLocaleString('id-ID')}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowConfirm(false)}>Batal</Button>
                  <Button className="bg-yellow-500 hover:bg-yellow-600" onClick={handleConfirmPO} data-testid="confirm-adj-btn">
                    Simpan & Cetak TTB
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
